import { Fragment, useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { all_routes } from "../../router/all_routes"
import {
  getActivityLogFilters,
  listActivityLogs,
  type ActivityLogFilters,
  type ActivityLogItem,
  type ActivityType,
} from "../../../service/activity-log"

const PAGE_SIZE = 50

const CATEGORY_LABELS: Record<ActivityType, string> = {
  PAGE_VIEW: "Page views",
  VIEW: "Record views",
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  LOGIN: "Sign-ins",
  LOGOUT: "Sign-outs",
  ACTION: "Other actions",
}

const CATEGORY_BADGE: Record<ActivityType, string> = {
  PAGE_VIEW: "badge-soft-secondary",
  VIEW: "badge-soft-primary",
  CREATE: "badge-soft-success",
  UPDATE: "badge-soft-info",
  DELETE: "badge-soft-danger",
  LOGIN: "badge-soft-secondary",
  LOGOUT: "badge-soft-secondary",
  ACTION: "badge-soft-warning",
}

// What a row is about when the record's name could not be resolved (e.g. it
// has since been deleted).
const ENTITY_NAMES: Record<string, string> = {
  enrollment: "Student",
  student: "Student",
  user: "Staff member",
  employee: "Staff member",
  admission: "Admission",
  registration: "Registration",
  invoice: "Invoice",
}

// Where the record a row is about can be opened. Students have a profile page;
// staff, admissions and invoices have no page addressable by id, so those rows
// name the record without linking to it.
const profileLink = (item: ActivityLogItem): string | null => {
  const enrollmentId =
    item.entityType === "enrollment"
      ? item.entityId
      : item.entityType === "student"
        ? item.enrollmentId
        : null
  if (enrollmentId) return all_routes.studentDetail.replace(":id", enrollmentId)
  if (item.entityType === "employee" && item.entityId) {
    return all_routes.staffAttendanceDetails.replace(":employeeId", item.entityId)
  }
  return null
}

type Filters = {
  user: string
  label: string
  type: string
  from: string
  to: string
  q: string
}

const EMPTY_FILTERS: Filters = { user: "", label: "", type: "", from: "", to: "", q: "" }

// "mobileNO" / "fatherPhoneNumber" -> "Mobile NO" / "Father Phone Number"
const fieldLabel = (field: string): string =>
  field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })

// The date inputs give a calendar day in the viewer's own timezone; send the
// instants that day starts and ends.
const dayStart = (date: string) => new Date(`${date}T00:00:00`).toISOString()
const dayEnd = (date: string) => new Date(`${date}T23:59:59.999`).toISOString()

const ActivityLog = () => {
  const [items, setItems] = useState<ActivityLogItem[]>([])
  const [options, setOptions] = useState<ActivityLogFilters | null>(null)
  // `draft` is what the form shows; `applied` is what the list was fetched
  // with, so typing in the search box doesn't refetch on every keystroke.
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listActivityLogs({
        page,
        limit: PAGE_SIZE,
        user: applied.user || undefined,
        label: applied.label || undefined,
        type: applied.type || undefined,
        from: applied.from ? dayStart(applied.from) : undefined,
        to: applied.to ? dayEnd(applied.to) : undefined,
        q: applied.q.trim() || undefined,
      })
      setItems(res?.data || [])
      setHasMore(!!res?.hasMore)
    } catch (e: any) {
      // Inline rather than a toast: a 403 here is the expected answer for
      // anyone who is not the owner.
      setError(e?.response?.data?.message || "Failed to load the activity log")
      setItems([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [applied, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    getActivityLogFilters()
      .then((res) => setOptions(res?.data || null))
      .catch(() => setOptions(null))
  }, [])

  const apply = (next: Filters) => {
    setDraft(next)
    setApplied(next)
    setPage(1)
    setOpenId(null)
  }

  // Dropdowns and dates apply immediately; the search box waits for Enter.
  const change = (key: keyof Filters, value: string) => apply({ ...draft, [key]: value })

  const filtered = Object.values(applied).some(Boolean)

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Activity Log</h3>
            <p className="text-muted mb-0">
              Who did what, and when — pages opened, records viewed, and everything created,
              changed or deleted. Visible to the owner only.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-body pb-1">
            <form
              className="row g-2 align-items-end"
              onSubmit={(e) => {
                e.preventDefault()
                apply(draft)
              }}
            >
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">User</label>
                <select
                  className="form-select"
                  value={draft.user}
                  onChange={(e) => change("user", e.target.value)}
                >
                  <option value="">All users</option>
                  {options?.users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name}
                      {u.role ? ` (${u.role})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">Activity</label>
                <select
                  className="form-select"
                  value={draft.label}
                  onChange={(e) => change("label", e.target.value)}
                >
                  <option value="">All activity</option>
                  {options?.labels.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={draft.type}
                  onChange={(e) => change("type", e.target.value)}
                >
                  <option value="">All categories</option>
                  {(Object.keys(CATEGORY_LABELS) as ActivityType[]).map((type) => (
                    <option key={type} value={type}>
                      {CATEGORY_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={draft.from}
                  max={draft.to || undefined}
                  onChange={(e) => change("from", e.target.value)}
                />
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={draft.to}
                  min={draft.from || undefined}
                  onChange={(e) => change("to", e.target.value)}
                />
              </div>
              <div className="col-xl-2 col-md-4 col-sm-6">
                <label className="form-label">Search</label>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Student, page, invoice…"
                  value={draft.q}
                  onChange={(e) => setDraft({ ...draft, q: e.target.value })}
                />
              </div>
              <div className="col-12 d-flex gap-2 mb-2">
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  disabled={loading || (!filtered && !draft.q)}
                  onClick={() => apply(EMPTY_FILTERS)}
                >
                  Clear filters
                </button>
                <button
                  type="button"
                  className="btn btn-light btn-sm ms-auto"
                  disabled={loading}
                  onClick={load}
                >
                  <i className="ti ti-refresh me-1" />
                  Refresh
                </button>
              </div>
            </form>
          </div>
        </div>

        {error ? (
          <div className="card">
            <div className="card-body text-center text-danger py-5">{error}</div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 190 }}>When</th>
                      <th>User</th>
                      <th>Activity</th>
                      <th>Details</th>
                      <th>Campus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const expandable = !!item.changes?.length
                      const open = openId === item._id
                      const target =
                        item.entityLabel ||
                        (item.entityType ? ENTITY_NAMES[item.entityType] || "" : "")
                      const link = profileLink(item)
                      return (
                        <Fragment key={item._id}>
                          <tr
                            onClick={
                              expandable ? () => setOpenId(open ? null : item._id) : undefined
                            }
                            style={expandable ? { cursor: "pointer" } : undefined}
                          >
                            <td className="text-nowrap">{formatTime(item.at)}</td>
                            <td>
                              <div className="fw-medium">{item.userName || "—"}</div>
                              {item.userRole && (
                                <div className="text-muted small">{item.userRole}</div>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${CATEGORY_BADGE[item.type]}`}>
                                {item.label}
                              </span>
                              {item.outcome === "denied" && (
                                <span className="badge badge-soft-danger ms-1">Not allowed</span>
                              )}
                              {item.outcome === "failed" && (
                                <span className="badge badge-soft-warning ms-1">Failed</span>
                              )}
                              {item.source === "backfill" && (
                                <span
                                  className="badge badge-soft-secondary ms-1"
                                  title="Reconstructed from records that existed before activity logging was switched on"
                                >
                                  Earlier record
                                </span>
                              )}
                            </td>
                            <td className="text-wrap" style={{ minWidth: 240 }}>
                              {(target || item.page) && (
                                <div className="fw-medium">
                                  {target && link ? (
                                    <Link
                                      to={link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={
                                        item.entityType === "employee"
                                          ? "Open attendance record"
                                          : "Open profile"
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {target}
                                      <i className="ti ti-external-link ms-1" />
                                    </Link>
                                  ) : (
                                    target || item.page
                                  )}
                                </div>
                              )}
                              {target && item.page && (
                                <div className="text-muted small">{item.page}</div>
                              )}
                              {item.summary && (
                                <div className="text-muted small">{item.summary}</div>
                              )}
                              {expandable && (
                                <div className="small text-primary">
                                  <i
                                    className={`ti ${open ? "ti-chevron-down" : "ti-chevron-right"} me-1`}
                                  />
                                  {item.changes!.length} field
                                  {item.changes!.length === 1 ? "" : "s"} changed
                                </div>
                              )}
                              {!target && !item.page && !item.summary && !expandable && (
                                <span className="text-muted small">
                                  {item.type === "LOGIN" || item.type === "LOGOUT"
                                    ? item.ip
                                      ? `From ${item.ip}`
                                      : "—"
                                    : item.path || "—"}
                                </span>
                              )}
                            </td>
                            <td className="text-nowrap">{item.branch?.name || "—"}</td>
                          </tr>
                          {expandable && open && (
                            <tr>
                              <td colSpan={5} className="bg-light">
                                <table className="table table-sm table-bordered bg-white mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ width: "28%" }}>Field</th>
                                      <th style={{ width: "36%" }}>Before</th>
                                      <th style={{ width: "36%" }}>After</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.changes!.map((change, idx) => (
                                      <tr key={`${change.record}-${change.field}-${idx}`}>
                                        <td>
                                          {fieldLabel(change.field)}
                                          {change.record && (
                                            <span className="text-muted small ms-1">
                                              ({change.record})
                                            </span>
                                          )}
                                        </td>
                                        <td className="text-break">{change.from || "—"}</td>
                                        <td className="text-break">{change.to || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {item.changesTruncated && (
                                  <div className="text-muted small mt-2">
                                    This request changed more fields than are listed here.
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                    {!loading && items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-5">
                          {filtered
                            ? "No activity matches these filters."
                            : "No activity has been recorded yet."}
                        </td>
                      </tr>
                    )}
                    {loading && items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-5">
                          Loading…
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <span className="text-muted small">
                Page {page}
                {items.length > 0 && ` · ${items.length} entr${items.length === 1 ? "y" : "ies"}`}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-light btn-sm"
                  disabled={loading || page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Newer
                </button>
                <button
                  className="btn btn-light btn-sm"
                  disabled={loading || !hasMore}
                  onClick={() => setPage(page + 1)}
                >
                  Older
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ActivityLog
